<template>
  <div
    :class="[
      $style.base,
      {
        [$style.alignLeft]: alignLeft,
        [$style.alignBottom]: alignBottom,
        [$style.inline]: inline,
        [$style.small]: size === 's'
      }
    ]"
  >
    <div
      :class="$style.inner"
      ref="container"
    >
      <div
        v-if="listOptions.length === 1 || inline"
        :class="$style.inlineActions"
      >
        <BaseIcon
          v-for="(option, index) in listOptions"
          :key="`inline-${index}`"
          :name="option.icon"
          :tooltip="option.label"
          :color="color === 'dark' ? 'fixed' : color"
          :routerLink="option.routerLink"
          :background="background"
          :size="size"
          @click.stop.prevent="onOptionClick(option)"
          v-test="`_base-more-option-${option.name}`"
        />
      </div>
      <BaseIcon
        v-else
        name="more-horizontal"
        :color="color === 'dark' ? 'fixed' : color"
        :background="background"
        size="l"
        @click.stop.prevent="onIconClick"
        v-test="'_base-more-dots'"
      />
      <div
        v-show="isExpanded"
        :class="$style.listOptions"
        @click="isExpanded = false"
        v-test="'_base-more-list'"
      >
        <LinkElement
          v-for="(option, index) in listOptions"
          :key="index"
          :class="$style.listOption"
          :routerLink="option.routerLink"
          @click.stop.prevent="onOptionClick(option)"
          v-test="`_base-more-option-${option.name}`"
        >
          <BaseIcon :name="option.icon" />
          {{ option.label }}
        </LinkElement>
      </div>
    </div>
  </div>
</template>

<script
  lang="ts"
  setup
>
import { onClickOutside } from '@vueuse/core';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { LocationQueryRaw } from 'vue-router';
import LinkElement from '@/components/_shared/Link.vue';
import type { IconName } from '@/components/base-icon/types';

const actions: { name: string; icon: IconName }[] = [
  {
    name: 'edit',
    icon: 'edit',
  },
  {
    name: 'delete',
    icon: 'delete',
  },
  {
    name: 'remove',
    icon: 'delete',
  },
  {
    name: 'merge',
    icon: 'person-add',
  },
  {
    name: 'view',
    icon: 'eye',
  },
  {
    name: 'restore',
    icon: 'undo',
  },
  {
    name: 'call',
    icon: 'phone',
  },
  {
    name: 'email',
    icon: 'email',
  },
  {
    name: 'complete',
    icon: 'check',
  },
  {
    name: 'duplicate',
    icon: 'copy',
  },
  {
    name: 'edit',
    icon: 'edit',
  },

  {
    name: 'create',
    icon: 'edit',
  },
  {
    name: 'expand',
    icon: 'edit',
  },
  {
    name: 'delete',
    icon: 'delete',
  },
  {
    name: 'pdf',
    icon: 'pdf',
  },
  {
    name: 'fill',
    icon: 'plus',
  },
  {
    name: 'print',
    icon: 'printer',
  },
  {
    name: 'send',
    icon: 'paper-plane',
  },
  {
    name: 'resend',
    icon: 'arrow-corner-up-right',
  },
  {
    name: 'archive',
    icon: 'archive',
  },
  {
    name: 'unarchive',
    icon: 'archive',
  },
  {
    name: 'pay',
    icon: 'barcode',
  },
  {
    name: 'sort',
    icon: 'swap-vertical',
  },
  {
    name: 'export',
    icon: 'download',
  },
];

defineOptions({
  inheritAttrs: false,
});

export type Option = {
  name: string;
  icon: IconName;
  label: string;
  routerLink?: LocationQueryRaw;
};

const props = withDefaults(
  defineProps<{
    alignBottom?: boolean;
    alignLeft?: boolean;
    background?: 'default' | 'transparent';
    color?: 'primary' | 'dark';
    options: string[] | Option[];
    inline?: boolean;
    size?: 's' | 'm' | 'l';
  }>(),
  {
    alignBottom: false,
    alignLeft: false,
    background: 'default',
    color: 'primary',
    inline: false,
    options: () => [],
    size: 'm',
  },
);

const emit = defineEmits(['select']);

const isExpanded = ref(false);
const container = ref();
onClickOutside(container, () => {
  isExpanded.value = false;
});

const { t } = useI18n();

const listOptions = computed<Option[]>(() => {
  return props.options
    .map((option) => {
      if (typeof option === 'string') {
        const action = actions.find((action) => action.name === option);
        return action
          ? {
              name: option,
              label: t(`global.actions.${action.name}`),
              icon: action.icon,
            }
          : null;
      } else {
        const action = actions.find((action) => action.name === option.name);
        return {
          name: option.name || action?.name,
          label: option.label || t(`global.actions.${action?.name}`),
          icon: option.icon || action?.icon,
          routerLink: option.routerLink,
        };
      }
    })
    .filter((option) => !!option);
});

const onIconClick = () => {
  isExpanded.value = !isExpanded.value;
};

const onOptionClick = (option: Option) => {
  emit('select', option.name);
  isExpanded.value = false;
};
</script>

<style
  lang="scss"
  module
>
$size: 20px;

.base {
  display: inline-block;
  flex-shrink: 0;
}

.inner {
  position: relative;
  display: flex;
  align-items: center;
}

.listOptions {
  position: absolute;
  background: var(--surface-primary);
  box-shadow: var(--shadow);
  border-radius: $radius;
  overflow: hidden;
  z-index: 10;

  .base.alignLeft & {
    left: calc(50% - #{$size * 0.5});
  }

  .base:not(.alignLeft) & {
    right: calc(50% - #{$size * 0.5});
  }

  .base:not(.alignBottom) & {
    top: calc(50% + #{$spacing * 0.5});
  }

  .base.alignBottom & {
    bottom: calc(50% + #{$spacing * 0.5});
  }
}

.listOption {
  display: flex;
  align-items: center;
  font-size: 12px;
  margin-left: -2px;
  font-weight: normal;
  white-space: nowrap;
  padding: 12px;
  cursor: pointer;

  &:not(:last-child) {
    border-bottom: 1px solid var(--border);
  }

  @include hover {
    background: var(--highlight);
  }

  &:first-child {
    border-radius: $radius $radius 0 0;
  }

  &:last-child {
    border-radius: 0 0 $radius $radius;
  }

  & > *:first-child {
    margin-right: $spacing * 0.25;
  }
}

.inlineActions {
  display: flex;
  gap: 6px;
}
</style>
